import React, { useState } from 'react';
import InfoCallout from '../DesignSystem/InfoCallout';
import InfoCalloutToneEnum from '../../Enums/InfoCalloutToneEnum';

// Splits "a **b** c" into ['a ', <b>b</b>, ' c'] so plain-TS window descriptions
// can express emphasis without JSX.
const parseBoldMarkers = (text) =>
  text.split(/\*\*(.+?)\*\*/g).map((part, index) => (index % 2 === 1 ? <b key={index}>{part}</b> : part));

// Generic wizard input for the InfoCallout type. The window description supplies
// a buildContent(item) callback returning { paragraphs, facts?, chips?, chipsLabel?, imageSrc?, imageAlt? } —
// paragraph and fact strings may contain **bold** markers. Dismissal is local state, so
// the card returns on the next wizard open.
//
// A fact is one scope line — { value, label, detail, tone?, muted? }. It exists because a
// bare sentence like "target rates apply to the 0 checked countries" reads as an error
// report rather than as a scope: the badge carries the figure, the label says which part of
// the form it governs, the detail names the control that moves it, and the tone paints the
// badge in that control's own colour. A count of zero is a legitimate state, so it goes
// muted rather than coloured — nothing is wrong, that part is simply not in scope yet.
function InfoCalloutField({
  paragraphs = null,
  facts = null,
  chips = null,
  chipsLabel = 'countries',
  imageSrc = null,
  imageAlt = '',
}) {
  const [dismissed, setDismissed] = useState(false);
  const paragraphList = paragraphs ?? [];
  const factList = facts ?? [];
  if (dismissed || (paragraphList.length === 0 && factList.length === 0 && !imageSrc)) return null;

  return (
    <InfoCallout
      chips={chips}
      chipsLabel={chipsLabel}
      onDismiss={() => setDismissed(true)}
    >
      {paragraphList.map((paragraph, index) => (
        <p key={index}>{parseBoldMarkers(paragraph)}</p>
      ))}
      {factList.length > 0 && (
        <ul className='mie-facts'>
          {factList.map((fact, index) => (
            <li
              className={fact.muted ? 'mie-fact muted' : 'mie-fact'}
              key={index}
            >
              <span className={`mie-fact-badge ${fact.tone ?? InfoCalloutToneEnum.Neutral}`}>{fact.value}</span>
              <span className='mie-fact-text'>
                <b className='mie-fact-label'>{fact.label}</b>
                {/* A colon, not a dash: a zero-count detail already contains its own dash
                    ("not in scope yet — tick …"), and two in one line read as a stutter. */}
                {fact.detail ? ': ' : ''}
                {fact.detail ? parseBoldMarkers(fact.detail) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
      {imageSrc && (
        <img
          src={imageSrc}
          alt={imageAlt}
        />
      )}
    </InfoCallout>
  );
}

export default InfoCalloutField;
